import { Box, Button, TextField, Typography, styled } from "@mui/material";
import { makeStyles, createStyles } from "@mui/styles";
import type { Theme } from "@mui/material/styles";
import { Link } from "react-router-dom";

export const Container = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  width: "100%",
  margin: 0,
  padding: 0,
  boxShadow: "none",
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  boxSizing: "border-box",
  [theme.breakpoints.down("md")]: {
    padding: "0px 30px",
  },
}));

export const LogoBox = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginBottom: "40px",
  maxWidth: "559px",
  justifyContent: "center",
}));

export const Form = styled("form")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100vw",
  maxWidth: "500px",
  padding: "0px 30px",
}));

export const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-input": {
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
    fontSize: "20px",
    "&:-webkit-autofill": {
      transitionDelay: "9999s",
      transitionProperty: "background-color, color",
    },
  },
  "& .MuiFilledInput-root": {
    border: "1px solid white",
    borderRadius: 4,
    "&.Mui-focused": {
      border: "2px solid #fc4c4c",
    },
  },
  "& .MuiInputLabel-root": {
    color: theme.palette.primary.contrastText,
    "&.Mui-focused": {
      color: theme.palette.primary.contrastText,
    },
  },
  "& label.Mui-focused": {
    color: theme.palette.primary.contrastText,
  },
  "& .MuiFormHelperText-root.Mui-error": {
    paddingTop: "6px",
    fontSize: "14px",
    margin: "10px 0px",
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.error.main,
  },
  "& .MuiFormLabel-root.Mui-error": {
    fontSize: "16px",
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.primary.contrastText,
  },
  "& p.css-14ieswp-MuiFormHelperText-root": {
    paddingTop: "6px",
    margin: "10px 0px",
    fontSize: "14px",
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.primary.contrastText,
  },
  "& .css-14ieswp-MuiFormHelperText-root.Mui-error": {
    color: theme.palette.error.main,
  },
}));

export const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  marginTop: "15px",
  height: "48px",
  background: theme.palette.primary.light,
  color: theme.palette.primary.main,
  "&:hover": {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
  },
  "&.Mui-disabled": {
    background: theme.palette.primary.light,
    color: "rgba(0, 0, 0, 0.38)",
  },
}));

export const LinkText = styled(Typography)<{ component?: React.ElementType; to?: string }>(
  ({ theme }) => ({
    color: theme.palette.primary.contrastText,
    fontSize: 16,
    display: "flex",
    justifyContent: "center",
    paddingTop: "10px",
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  }),
);

export const RegisterText = styled(Typography)<{ component?: React.ElementType; to?: string }>(
  ({ theme }) => ({
    color: theme.palette.secondary.main,
    fontSize: 14,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  }),
);

export const EmailList = styled(Box)(({ theme }) => ({
  padding: "10px",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.secondary.main,
  },
  "&:focus": {
    backgroundColor: "red",
  },
}));

export const fadeIn = {
  animation: "fadeIn 0.3s ease-in",
  "@keyframes fadeIn": {
    from: {
      opacity: 0,
      transform: "translateY(20px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
};

// Keep the existing LoginStyles for backward compatibility
export const LoginStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      minHeight: "100vh",
      width: "100%",
      margin: 0,
      padding: 0,
      boxShadow: "none",
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      boxSizing: "border-box",
      [theme.breakpoints.down("md")]: {
        padding: "0px 30px",
      },
    },
    loginLinkStyle: {
      color: theme.palette.secondary.main,
      fontSize: 14,
    },
    formStyles: {
      display: "flex",
      flexDirection: "column",
      width: "100vw",
      maxWidth: "500px",
      padding: "0px 30px",
    },
    btnStyle: {
      width: "100%",
    },
    logoBox: {
      textAlign: "center",
      marginBottom: "40px",
      maxWidth: "559px",
      justifyContent: "center",
    },
    label: {
      color: theme.palette.primary.contrastText,
      fontSize: "16px",
      fontWeight: "500",
      marginTop: "7px",
    },
    forgotPasswordStyle: {
      color: theme.palette.primary.contrastText,
      fontSize: 16,
      display: "flex",
      justifyContent: "center",
      paddingTop: "10px",
    },
    registerStyle: {
      color: theme.palette.secondary.main,
      fontSize: 14,
    },
    subTitle: {
      color: theme.palette.secondary.main,
      fontSize: 16,
      justifyContent: "center",
      alignItems: "center",
      paddingTop: "10px",
      bottom: "0",
      right: "20%",
      left: "20%",
    },
    root: {
      "& .MuiInputBase-input": {
        color: theme.palette.primary.contrastText,
        fontWeight: 600,
        fontSize: "20px",
        "&:-webkit-autofill": {
          transitionDelay: "9999s",
          transitionProperty: "background-color, color",
        },
      },
      "& .MuiFilledInput-root": {
        border: "1px solid white",
        borderRadius: 4,
      },
      "& .MuiFilledInput-root.Mui-focused": {
        border: "2px solid #fc4c4c",
        borderRadius: 4,
      },
      "& label.Mui-focused": {
        color: theme.palette.primary.contrastText,
      },
      "& .MuiFormHelperText-root.Mui-error": {
        paddingTop: "6px",
        fontSize: "14px",
        margin: "10px 0px",
        fontWeight: theme.typography.fontWeightMedium,
        color: theme.palette.error.main,
      },
      "& .MuiFormLabel-root.Mui-error": {
        fontSize: "16px",
        fontWeight: theme.typography.fontWeightMedium,
        color: theme.palette.primary.contrastText,
      },
      "& p.css-14ieswp-MuiFormHelperText-root": {
        paddingTop: "6px",
        margin: "10px 0px",
        fontSize: "14px",
        fontWeight: theme.typography.fontWeightMedium,
        color: theme.palette.primary.contrastText,
      },
      "& .css-14ieswp-MuiFormHelperText-root.Mui-error": {
        color: theme.palette.error.main,
      },
    },
    button: {
      textTransform: "none",
      marginTop: "15px",
      height: "48px",
      background: theme.palette.primary.light,
      color: theme.palette.primary.main,
      "&:hover": {
        backgroundColor: theme.palette.primary.contrastText,
        color: theme.palette.primary.main,
      },
      "&.Mui-disabled": {
        background: theme.palette.primary.light,
        color: "rgba(0, 0, 0, 0.38)",
      },
    },
    heightTextField: {
      height: "70px",
    },
    errorMessage: {
      fontSize: "16px",
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.palette.error.main,
    },
    emailListStyle: {
      padding: "10px",
      "& :hover": {
        backgroundColor: theme.palette.secondary.main,
      },
      "& :focus": {
        backgroundColor: "red",
      },
    },
    fadeIn: {
      animation: "$fadeInAnimation 0.75s",
    },
    "@keyframes fadeInAnimation": {
      "0%": {
        opacity: 0,
        transform: "translateY(-20px)",
      },
      "100%": {
        opacity: 1,
        transform: "translateY(0)",
      },
    },
  }),
);

export const StyledForgotPasswordLink = styled(Link)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  fontSize: 16,
  display: "flex",
  justifyContent: "center",
  paddingTop: "10px",
  textDecoration: "underline",
}));

export const StyledFooterTypography = styled(Typography)(({ theme }) => ({
  textAlign: "center",
  paddingLeft: "25px",
  paddingRight: "25px",
  paddingTop: "20px",
  marginBottom: "20px",
  fontSize: 14,
  color: theme.palette.primary.contrastText,
}));

export const StyledSignUpLink = styled(Link)(({ theme }) => ({
  color: theme.palette.error.main,
  textDecoration: "none",
  "&:hover": {
    textDecoration: "underline",
  },
}));
